// Icon rendering utilities for game map editor
// These functions draw icons at fixed screen sizes independent of canvas zoom

/**
 * Draw a door icon at a given position
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} midX - World x coordinate
 * @param {number} midY - World y coordinate
 * @param {number} gridSize
 * @param {number} zoom
 * @param {boolean} isOpen - Whether the door is open
 * @param {boolean} [isSelected=false] - Whether the door is selected
 */
export function drawDoorIcon(ctx, midX, midY, gridSize, zoom, isOpen, isSelected = false) {
  const screenGridSize = gridSize * zoom;
  /** @type {number} */
  let iconSize;
  if (screenGridSize >= 90) {
    iconSize = 45;
  } else if (screenGridSize >= 60) {
    iconSize = 30;
  } else if (screenGridSize >= 30) {
    iconSize = 15;
  } else {
    return;
  }

  const backgroundSize = iconSize + 6;

  const transform = ctx.getTransform();
  const screenX = midX * transform.a + transform.e;
  const screenY = midY * transform.d + transform.f;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  // Draw background
  ctx.globalAlpha = 0.7;
  ctx.fillStyle = isSelected ? '#0d6efd' : 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(screenX - backgroundSize / 2, screenY - backgroundSize / 2, backgroundSize, backgroundSize);

  // Set icon drawing properties
  ctx.globalAlpha = 0.8;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = iconSize === 45 ? 2 : iconSize === 30 ? 1.5 : 1;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const scale = iconSize / 24;
  ctx.translate(screenX, screenY);
  ctx.scale(scale, scale);
  ctx.translate(-12, -12);

  const paths = isOpen
    ? [
        'M11 20H2',
        'M11 4.562v16.157a1 1 0 0 0 1.242.97L19 20V5.562a2 2 0 0 0-1.515-1.94l-4-1A2 2 0 0 0 11 4.561z',
        'M11 4H8a2 2 0 0 0-2 2v14',
        'M14 12h.01',
        'M22 20h-3',
      ]
    : ['M10 12h.01', 'M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14', 'M2 20h20'];

  for (const pathData of paths) {
    const path = new Path2D(pathData);
    ctx.stroke(path);
  }

  ctx.restore();
}

/**
 * Draw a window icon at a given position
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} midX - World x coordinate
 * @param {number} midY - World y coordinate
 * @param {number} gridSize
 * @param {number} zoom
 */
export function drawWindowIcon(ctx, midX, midY, gridSize, zoom) {
  const screenGridSize = gridSize * zoom;
  /** @type {number} */
  let iconSize;
  if (screenGridSize >= 90) {
    iconSize = 45;
  } else if (screenGridSize >= 60) {
    iconSize = 30;
  } else if (screenGridSize >= 30) {
    iconSize = 15;
  } else {
    return;
  }

  const backgroundSize = iconSize + 6;

  const transform = ctx.getTransform();
  const screenX = midX * transform.a + transform.e;
  const screenY = midY * transform.d + transform.f;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  // Draw background
  ctx.globalAlpha = 0.7;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(screenX - backgroundSize / 2, screenY - backgroundSize / 2, backgroundSize, backgroundSize);

  // Set icon drawing properties
  ctx.globalAlpha = 0.8;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = iconSize === 45 ? 2 : iconSize === 30 ? 1.5 : 1;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.translate(screenX, screenY);

  // Draw Grid2X2 icon (4 squares)
  const squareSize = iconSize === 45 ? 15 : iconSize === 30 ? 10 : 5;
  const spacing = iconSize === 45 ? 3 : iconSize === 30 ? 2 : 1;

  ctx.strokeRect(-squareSize - spacing, -squareSize - spacing, squareSize, squareSize);
  ctx.strokeRect(spacing, -squareSize - spacing, squareSize, squareSize);
  ctx.strokeRect(-squareSize - spacing, spacing, squareSize, squareSize);
  ctx.strokeRect(spacing, spacing, squareSize, squareSize);

  ctx.restore();
}

/**
 * Draw a flame icon at a given position
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - World x coordinate
 * @param {number} y - World y coordinate
 * @param {number} gridSize
 * @param {number} zoom
 * @param {boolean} isOn - Whether the light is on
 * @param {boolean} [isSelected=false]
 * @param {boolean} [isHovered=false]
 */
export function drawFlameIcon(ctx, x, y, gridSize, zoom, isOn, isSelected = false, isHovered = false) {
  const screenGridSize = gridSize * zoom;
  /** @type {number} */
  let iconSize;
  if (screenGridSize >= 90) {
    iconSize = 45;
  } else if (screenGridSize >= 60) {
    iconSize = 30;
  } else if (screenGridSize >= 30) {
    iconSize = 15;
  } else {
    return;
  }

  const backgroundSize = iconSize + 6;

  const transform = ctx.getTransform();
  const screenX = x * transform.a + transform.e;
  const screenY = y * transform.d + transform.f;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  // Draw background
  ctx.globalAlpha = 0.7;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(screenX - backgroundSize / 2, screenY - backgroundSize / 2, backgroundSize, backgroundSize);

  // Set icon drawing properties
  ctx.globalAlpha = 0.8;

  const shouldFill = isSelected || isHovered;
  const iconColor = shouldFill ? '#ffdd00' : '#ffffff';

  if (shouldFill) {
    ctx.fillStyle = iconColor;
  } else {
    ctx.strokeStyle = iconColor;
  }
  ctx.lineWidth = iconSize === 45 ? 2 : iconSize === 30 ? 1.5 : 1;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const scale = iconSize / 24;
  ctx.translate(screenX, screenY);
  ctx.scale(scale, scale);
  ctx.translate(-12, -12);

  // Flame SVG path (Lucide)
  const flamePath = new Path2D(
    'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z',
  );
  if (shouldFill) {
    ctx.fill(flamePath);
  } else {
    ctx.stroke(flamePath);
  }

  // If light is off, draw ban icon overlay
  if (!isOn) {
    const overlaySize = iconSize === 45 ? 18 : iconSize === 30 ? 12 : 6;
    const overlayX = 18;
    const overlayY = 18;

    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(overlayX, overlayY, overlaySize / 2 + 1, 0, 2 * Math.PI);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = iconSize === 45 ? 2 : iconSize === 30 ? 1.5 : 1;

    ctx.beginPath();
    ctx.arc(overlayX, overlayY, overlaySize / 2, 0, 2 * Math.PI);
    ctx.stroke();

    const slashOffset = (overlaySize / 2) * 0.7;
    ctx.beginPath();
    ctx.moveTo(overlayX - slashOffset, overlayY - slashOffset);
    ctx.lineTo(overlayX + slashOffset, overlayY + slashOffset);
    ctx.stroke();
  }

  ctx.restore();
}
